import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faSpinner, faCheckCircle, faExclamationTriangle,
    faUserPlus, faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import MemberPhotoAvatar from './MemberPhotoAvatar';

import { db, storage } from '../firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { memberService, familyService } from '../services';
import { validateMemberForm } from '../services/validation';

import './FormPage.css';

// Helper functions for birthday dropdowns
const getMonths = () => Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(0, i).toLocaleString('default', { month: 'long' }) }));
const getDaysInMonth = (month, year) => {
    if (!month) return Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
    const yearToUse = (year && parseInt(year, 10)) ? parseInt(year, 10) : 2000;
    const days = new Date(yearToUse, parseInt(month, 10), 0).getDate();
    return Array.from({ length: days }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
};
const getYears = () => {
    const years = [{ value: '', label: 'Year (Optional)' }];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= currentYear - 120; i--) {
        years.push({ value: i, label: i.toString() });
    }
    return years;
};

function AddMemberPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const passedState = location.state;

    // Form fields state
    const [selectedFamilyId, setSelectedFamilyId] = useState('');
    const [memberName, setMemberName] = useState('');
    const [selectedBirthMonth, setSelectedBirthMonth] = useState('');
    const [selectedBirthDay, setSelectedBirthDay] = useState('');
    const [selectedBirthYear, setSelectedBirthYear] = useState('');
    const [cellphone, setCellphone] = useState('');
    const [email, setEmail] = useState('');
    const [currentPhotoPath, setCurrentPhotoPath] = useState(null);

    // Component/Data state
    const [familiesList, setFamiliesList] = useState([]);
    const [preselectedFamily, setPreselectedFamily] = useState(null);
    const [loadingFamilies, setLoadingFamilies] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);

    // Extracted photos state
    const [extractedPhotos, setExtractedPhotos] = useState([]);
    const [loadingPhotos, setLoadingPhotos] = useState(false);
    const [isExtracting, setIsExtracting] = useState(false);
    const [lastFacesExtractedAt, setLastFacesExtractedAt] = useState(null);
    const { loadingAuth, userOrgId } = useAuth();

    // Automation status
    const [inviteAutomationEnabled, setInviteAutomationEnabled] = useState(false);

    // Check invite automation status
    useEffect(() => {
        if (!userOrgId) return;
        const automationDocId = `${userOrgId}__member_invite`;
        getDoc(doc(db, 'automations', automationDocId)).then((snap) => {
            setInviteAutomationEnabled(snap.exists() && snap.data().enabled === true);
        }).catch(() => {
            setInviteAutomationEnabled(false);
        });
    }, [userOrgId]);

    // Check auth status
    useEffect(() => {
        const checkAuth = async () => {
            try {
                await memberService.getOrgId();
                setIsReady(true);
            } catch (err) {
                setError(err.message || "Could not determine organization ID. Please ensure you are logged in.");
                setLoadingFamilies(false);
                setIsReady(false);
            } finally {
                setCheckingAuth(false);
            }
        };
        checkAuth();
    }, []);

    // Handle passed state from navigation
    useEffect(() => {
        const { preselectedFamilyId, preselectedFamilyName } = passedState || {};

        if (preselectedFamilyId && preselectedFamilyName) {
            setPreselectedFamily({ id: preselectedFamilyId, name: preselectedFamilyName });
            setSelectedFamilyId(preselectedFamilyId);
            setLoadingFamilies(false);
        } else {
            setPreselectedFamily(null);
        }
    }, [passedState]);

    // Fetch Families if not pre-selected
    useEffect(() => {
        if (!isReady || preselectedFamily) {
            setLoadingFamilies(false);
            return;
        }

        setLoadingFamilies(true);
        const fetchFamilies = async () => {
            try {
                const families = await familyService.getFamilies();
                const formattedFamilies = families.map(family => ({
                    id: family.id,
                    display: `${family.surname || 'N/A'} - ${family.street || 'No Street'}`
                }));
                setFamiliesList(formattedFamilies);
            } catch (err) {
                setError("Failed to load families list.");
            } finally {
                setLoadingFamilies(false);
            }
        };
        fetchFamilies();
    }, [isReady, preselectedFamily]);

    // Listen to family document for extraction status
    useEffect(() => {
        if (loadingAuth || !selectedFamilyId) return;

        const familyDocRef = doc(db, 'family', selectedFamilyId);
        const unsubscribe = onSnapshot(familyDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const imageUpdatedAt = data.imageUpdatedAt?.toMillis?.() || 0;
                const facesExtractedAt = data.facesExtractedAt?.toMillis?.() || 0;
                const EXTRACTION_TIMEOUT_MS = 10 * 60 * 1000;
                setIsExtracting(
                    imageUpdatedAt > 0 &&
                    imageUpdatedAt > facesExtractedAt &&
                    (Date.now() - imageUpdatedAt) < EXTRACTION_TIMEOUT_MS
                );
                setLastFacesExtractedAt(data.facesExtractedAt?.toMillis?.() || null);
            }
        }, (err) => {
            console.error("Error listening to family for extraction status:", err);
        });

        return () => unsubscribe();
    }, [selectedFamilyId, loadingAuth]);

    // Fetch extracted photos from Storage
    useEffect(() => {
        if (!selectedFamilyId) {
            setExtractedPhotos([]);
            return;
        }

        const fetchPhotos = async () => {
            setLoadingPhotos(true);
            try {
                const orgId = await memberService.getOrgId();
                const photosPath = `${orgId}/family/${selectedFamilyId}/members/`;
                const listRef = ref(storage, photosPath);
                const res = await listAll(listRef);
                const photoPromises = res.items.map(async (itemRef) => ({
                    path: itemRef.fullPath,
                    url: await getDownloadURL(itemRef),
                }));
                const photosData = await Promise.all(photoPromises);
                setExtractedPhotos(photosData);
            } catch (err) {
                if (err.code !== 'storage/object-not-found') {
                    console.error("Error fetching extracted photos:", err);
                }
                setExtractedPhotos([]);
            } finally {
                setLoadingPhotos(false);
            }
        };

        fetchPhotos();
    }, [selectedFamilyId, lastFacesExtractedAt]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setSuccessMessage(null);

        if (!selectedFamilyId) {
            setError("A family must be selected.");
            return;
        }

        const formData = {
            name: memberName,
            email: email,
            cellphone: cellphone,
            birthdayMonth: selectedBirthMonth,
            birthdayDay: selectedBirthDay,
            birthdayYear: selectedBirthYear,
        };

        const validation = validateMemberForm(formData);
        if (!validation.isValid) {
            const firstError = Object.values(validation.errors)[0];
            setError(firstError);
            return;
        }

        setSubmitting(true);
        try {
            const result = await memberService.createMember({
                familyId: selectedFamilyId,
                name: memberName.trim(),
                birthdayMonth: selectedBirthMonth || null,
                birthdayDay: selectedBirthDay || null,
                birthdayYear: selectedBirthYear || null,
                cellphone: cellphone.trim() || null,
                email: email.trim() || null,
                photo: currentPhotoPath,
            });

            if (result.success) {
                setSuccessMessage(result.message || "Member added successfully!");
                setMemberName('');
                setCellphone('');
                setEmail('');
                setCurrentPhotoPath(null);
                setSelectedBirthMonth('');
                setSelectedBirthDay('');
                setSelectedBirthYear('');
                if (!preselectedFamily) {
                    setSelectedFamilyId('');
                }

                const navigateTo = preselectedFamily ? `/families/${preselectedFamily.id}` : '/families';
                setTimeout(() => navigate(navigateTo, { state: { message: result.message } }), 1500);
            } else {
                throw new Error(result.error || "Failed to add member.");
            }
        } catch (err) {
            setError(err.message || "An unexpected error occurred.");
        } finally {
            setSubmitting(false);
        }
    };

    const availableMonths = useMemo(() => getMonths(), []);
    const availableDays = useMemo(() => getDaysInMonth(selectedBirthMonth, selectedBirthYear), [selectedBirthMonth, selectedBirthYear]);
    const availableYears = useMemo(() => getYears(), []);

    // Loading state
    if (checkingAuth) {
        return (
            <div className="form-page">
                <div className="form-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-form"></div>
                </div>
            </div>
        );
    }

    const backLink = preselectedFamily ? `/families/${preselectedFamily.id}` : '/families';

    return (
        <div className="form-page">
            {/* Header */}
            <div className="form-page-header">
                <div className="container">
                    <div className="form-page-header-content">
                        <div className="form-page-header-left">
                            <Link to={backLink} className="form-back-btn" title="Go Back">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="form-page-title">Add New Member</h1>
                                <p className="form-page-subtitle">
                                    {preselectedFamily ? `Adding to ${preselectedFamily.name} Family` : 'Add a member to a family'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="form-card">
                    <div className="form-card-body">
                        <form onSubmit={handleSubmit}>
                            {/* Alert Messages */}
                            {error && (
                                <div className="form-alert form-alert-danger">
                                    <div className="form-alert-icon">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                    </div>
                                    <div className="form-alert-content">{error}</div>
                                </div>
                            )}
                            {successMessage && (
                                <div className="form-alert form-alert-success">
                                    <div className="form-alert-icon">
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                    </div>
                                    <div className="form-alert-content">{successMessage}</div>
                                </div>
                            )}

                            {/* Photo Section - Inline extracted photos grid */}
                            {selectedFamilyId && (
                                <div className="form-section">
                                    <div className="form-section-title">Select Photo</div>
                                    {isExtracting && (
                                        <div className="form-extracting-banner">
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>Extracting faces from the latest family photo...</span>
                                        </div>
                                    )}
                                    {loadingPhotos ? (
                                        <div className="form-photos-loading">
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>Loading available photos...</span>
                                        </div>
                                    ) : extractedPhotos.length > 0 ? (
                                        <>
                                            <div className="form-photo-grid">
                                                {extractedPhotos.map(photo => (
                                                    <div
                                                        key={photo.path}
                                                        className={`form-photo-grid-item ${currentPhotoPath === photo.path ? 'selected' : ''}`}
                                                        onClick={() => setCurrentPhotoPath(currentPhotoPath === photo.path ? null : photo.path)}
                                                    >
                                                        <img src={photo.url} alt="Extracted face" />
                                                        {currentPhotoPath === photo.path && (
                                                            <div className="form-photo-grid-check">
                                                                <FontAwesomeIcon icon={faCheckCircle} />
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            {currentPhotoPath && (
                                                <div className="form-photo-selected-preview">
                                                    <MemberPhotoAvatar src={currentPhotoPath} alt="Selected" size={80} />
                                                    <span>Photo selected</span>
                                                </div>
                                            )}
                                        </>
                                    ) : !isExtracting ? (
                                        <div className="form-photos-empty">
                                            No photos available. Upload a family photo to auto-extract member photos.
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            {/* Family Selection */}
                            {!preselectedFamily && (
                                <div className="form-section">
                                    <div className="form-section-title">Family</div>
                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Select Family <span className="required">*</span>
                                        </label>
                                        <select
                                            className="form-field-select"
                                            value={selectedFamilyId}
                                            onChange={(e) => setSelectedFamilyId(e.target.value)}
                                            disabled={submitting || loadingFamilies}
                                            required
                                        >
                                            <option value="">-- Select Family --</option>
                                            {loadingFamilies ? (
                                                <option disabled>Loading families...</option>
                                            ) : (
                                                familiesList.map(family => (
                                                    <option key={family.id} value={family.id}>{family.display}</option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Basic Info Section */}
                            <div className="form-section">
                                <div className="form-section-title">Basic Information</div>

                                <div className="form-field">
                                    <label className="form-field-label">
                                        First Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-field-input"
                                        value={memberName}
                                        onChange={(e) => setMemberName(e.target.value)}
                                        placeholder="Enter first name"
                                        disabled={submitting}
                                        required
                                    />
                                </div>

                                <div className="form-field">
                                    <label className="form-field-label">Email Address</label>
                                    <input
                                        type="email"
                                        className="form-field-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="(Optional)"
                                        disabled={submitting}
                                    />
                                </div>

                                <div className="form-field">
                                    <label className="form-field-label">Cellphone</label>
                                    <input
                                        type="tel"
                                        className="form-field-input"
                                        value={cellphone}
                                        onChange={(e) => setCellphone(e.target.value)}
                                        placeholder="(Optional)"
                                        disabled={submitting}
                                    />
                                </div>
                            </div>

                            {/* Birthday Section */}
                            <div className="form-section">
                                <div className="form-section-title">Birthday (Optional)</div>

                                <div className="form-field-row form-field-row-date">
                                    <div className="form-field">
                                        <label className="form-field-label">Month</label>
                                        <select
                                            className="form-field-select"
                                            value={selectedBirthMonth}
                                            onChange={(e) => setSelectedBirthMonth(e.target.value)}
                                            disabled={submitting}
                                        >
                                            <option value="">Select Month</option>
                                            {availableMonths.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-field-label">Day</label>
                                        <select
                                            className="form-field-select"
                                            value={selectedBirthDay}
                                            onChange={(e) => setSelectedBirthDay(e.target.value)}
                                            disabled={submitting}
                                        >
                                            <option value="">Day</option>
                                            {availableDays.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-field-label">Year</label>
                                        <select
                                            className="form-field-select"
                                            value={selectedBirthYear}
                                            onChange={(e) => setSelectedBirthYear(e.target.value)}
                                            disabled={submitting}
                                        >
                                            {availableYears.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Invite email note */}
                            {inviteAutomationEnabled && email.trim() && (
                                <p className="form-field-hint" style={{ color: '#3b82f6', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '0.4rem' }} />
                                    This member will receive an invite email to create their account.
                                </p>
                            )}

                            {/* Submit Button */}
                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="form-submit-btn form-submit-btn-success"
                                    disabled={submitting || loadingFamilies}
                                >
                                    {submitting ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            Adding...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faUserPlus} />
                                            Add Member
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddMemberPage;
